"use client";

import { useEffect, useRef, useState } from "react";

interface NumberTickerProps {
  end: number;
  duration?: number;
  format?: (n: number) => string;
  className?: string;
}

export function NumberTicker({
  end,
  duration = 1800,
  format = (n) => Math.round(n).toLocaleString(),
  className,
}: NumberTickerProps) {
  // Server HTML carries the real figure (value === null renders `end`) so
  // crawlers and no-JS readers never see a placeholder zero; the animation
  // counts up from a nearby seed once the element scrolls into view.
  const [value, setValue] = useState<number | null>(null);
  const ref = useRef<HTMLSpanElement>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Reduced motion: leave value null so the static real figure renders.
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const seed = Math.floor(end * 0.85);
    const observer = new IntersectionObserver(
      (entries) => {
        if (startedRef.current) return;
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        startedRef.current = true;
        const start = performance.now();
        const tick = (now: number) => {
          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          // easeOutExpo
          const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
          setValue(seed + eased * (end - seed));
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration]);

  return (
    <span ref={ref} className={className} suppressHydrationWarning>
      {format(value ?? end)}
    </span>
  );
}
